'use client'

import React, { useState, useMemo } from 'react'
import Image from 'next/image'
import Link from 'next/link'
import { Search, X, ChevronDown, Loader2 } from 'lucide-react'
import type { Article, Media, VisaType, Team } from '@/payload-types'
import { useSearch } from '../../hooks/useSearch'
import { getMediaUrl } from '../../lib/media'

// Helper function to determine tag color based on content
function getTagVariant(label: string): string {
  const lowerLabel = label.toLowerCase()

  if (lowerLabel.includes('h-1b') || lowerLabel.includes('h1b')) {
    return 'orange'
  }
  if (lowerLabel.includes('green card') || lowerLabel.includes('low')) {
    return 'green'
  }
  if (lowerLabel.includes('policy') || lowerLabel.includes('update')) {
    return 'blue'
  }
  if (lowerLabel.includes('high') || lowerLabel.includes('urgent')) {
    return 'red'
  }
  if (lowerLabel.includes('medium')) {
    return 'orange'
  }
  if (lowerLabel.includes('announcement')) {
    return 'purple'
  }

  return 'default'
}

// Author headshot for the card bylines. Returns null when the author has no photo
// (or when the relation came back un-populated), so the byline falls back to the name alone.
function AuthorAvatar({
  author,
  sizeClass,
  sizes,
  ringClass = '',
}: {
  author: Team
  sizeClass: string
  sizes: string
  ringClass?: string
}) {
  const photo = author.photo && typeof author.photo !== 'number' ? author.photo : null
  const photoUrl = getMediaUrl(photo, 'thumbnail')

  if (!photoUrl) return null

  return (
    <span
      className={`relative ${sizeClass} shrink-0 aspect-square rounded-full overflow-hidden ${ringClass}`}
    >
      <Image src={photoUrl} alt={author.name || ''} fill sizes={sizes} className="object-cover" />
    </span>
  )
}

interface ArticleWithPreview extends Article {
  preview: string
}

interface ArticlesGridProps {
  articles: ArticleWithPreview[]
  authors: Team[]
}

export function ArticlesGrid({ articles, authors }: ArticlesGridProps) {
  const {
    query: searchQuery,
    setQuery: setSearchQuery,
    matchingIds,
    loading: searchLoading,
  } = useSearch({ collection: 'articles' })
  const [selectedAuthor, setSelectedAuthor] = useState<string>('')
  const [authorOpen, setAuthorOpen] = useState(false)

  // Filter articles based on search results and dropdown filters
  const filteredArticles = useMemo(() => {
    return articles.filter((article) => {
      // Search filter — when matchingIds is null (no query), show all
      const matchesSearch = matchingIds === null || matchingIds.has(article.id)

      // Author filter
      const articleAuthor = article.author as Team | null | undefined
      const matchesAuthor = !selectedAuthor || articleAuthor?.id?.toString() === selectedAuthor

      return matchesSearch && matchesAuthor
    })
  }, [articles, matchingIds, selectedAuthor])

  const clearFilters = () => {
    setSearchQuery('')
    setSelectedAuthor('')
  }

  const hasActiveFilters = searchQuery || selectedAuthor

  return (
    <div>
      {/* Search and Filters Bar */}
      <div className="mb-8 space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Search articles..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-12 pr-4 py-3 bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-blue/20 focus:border-brand-blue transition-all text-gray-900 placeholder:text-gray-500"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-600"
            >
              {searchLoading ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <X className="w-5 h-5" />
              )}
            </button>
          )}
        </div>

        {/* Filter Dropdowns */}
        <div className="flex flex-wrap gap-3">
          {/* Author Filter */}
          <div className="relative">
            <button
              onClick={() => setAuthorOpen(!authorOpen)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-all text-sm font-medium ${
                selectedAuthor
                  ? 'bg-brand-blue text-white border-brand-blue'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300'
              }`}
            >
              <span>
                {selectedAuthor
                  ? authors.find((a) => a.id.toString() === selectedAuthor)?.name || 'Author'
                  : 'Author'}
              </span>
              <ChevronDown
                className={`w-4 h-4 transition-transform ${authorOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {authorOpen && (
              <div className="absolute z-50 top-full mt-2 w-56 bg-white border border-gray-200 rounded-xl shadow-lg py-2 max-h-64 overflow-y-auto">
                <button
                  onClick={() => {
                    setSelectedAuthor('')
                    setAuthorOpen(false)
                  }}
                  className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                    !selectedAuthor ? 'text-brand-blue font-medium' : 'text-gray-700'
                  }`}
                >
                  All Authors
                </button>
                {authors.map((author) => (
                  <button
                    key={author.id}
                    onClick={() => {
                      setSelectedAuthor(author.id.toString())
                      setAuthorOpen(false)
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                      selectedAuthor === author.id.toString()
                        ? 'text-brand-blue font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    {author.name}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="flex items-center gap-1 px-4 py-2 text-sm text-gray-600 hover:text-gray-700 transition-colors"
            >
              <X className="w-4 h-4" />
              Clear filters
            </button>
          )}
        </div>
      </div>

      {/* Results Count */}
      <div className="mb-6 text-sm text-gray-600">
        {filteredArticles.length === articles.length
          ? `${articles.length} articles`
          : `${filteredArticles.length} of ${articles.length} articles`}
      </div>

      {/* No Results */}
      {filteredArticles.length === 0 && (
        <div className="py-16 text-center">
          <p className="text-gray-600 text-lg mb-4">No articles match your search criteria.</p>
          <button onClick={clearFilters} className="text-brand-blue hover:underline font-medium">
            Clear all filters
          </button>
        </div>
      )}

      {/* Bento Grid */}
      {filteredArticles.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 auto-rows-[280px]">
          {filteredArticles.map((article, index) => (
            <ArticleCard
              key={article.id}
              article={article}
              variant={getCardVariant(index)}
              priority={index < 3}
            />
          ))}
        </div>
      )}
    </div>
  )
}

// Determine card variant based on position in grid (3-column layout)
function getCardVariant(index: number): 'hero' | 'large' | 'standard' {
  if (index === 0) return 'hero'
  if (index === 3 || index === 6) return 'large' // Every 3rd row gets a wide card
  return 'standard'
}

// Unified Article Card Component
function ArticleCard({
  article,
  variant,
  priority = false,
}: {
  article: ArticleWithPreview
  variant: 'hero' | 'large' | 'standard'
  priority?: boolean
}) {
  const featuredImage = article.featuredImage as Media | null | undefined
  const imageUrl = getMediaUrl(featuredImage, 'card')
  const visaTypes = article.visaTypes as VisaType[] | null | undefined
  const author = article.author as Team | null | undefined

  // Grid span classes based on variant
  const gridClasses = {
    hero: 'md:col-span-2 lg:col-span-2 md:row-span-2',
    large: 'lg:col-span-2',
    standard: '',
  }

  // Hero variant - overlay style
  if (variant === 'hero') {
    return (
      <Link
        href={`/articles/${article.slug}`}
        className={`group relative bg-brand-blue rounded-2xl overflow-hidden ${gridClasses[variant]}`}
      >
        {/* Background Image */}
        {imageUrl && (
          <Image
            src={imageUrl}
            alt={article.title || ''}
            fill
            priority={priority}
            sizes="(max-width: 768px) 100vw, 66vw"
            className="object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
          />
        )}

        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-linear-to-t from-brand-blue via-brand-blue/80 to-transparent" />

        {/* Content */}
        <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
          {/* Tags */}
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className="text-xs px-3 py-1 rounded-full font-medium bg-brand-gold/20 text-brand-gold-light border border-brand-gold/30">
              Featured
            </span>
            {visaTypes && visaTypes.length > 0 && (
              <span className="text-xs px-3 py-1 rounded-full font-medium bg-white/10 text-white/90 border border-white/20">
                {visaTypes[0].name}
              </span>
            )}
          </div>

          {/* Title */}
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-serif text-white mb-4 group-hover:text-brand-gold-light transition-colors line-clamp-3">
            {article.title}
          </h2>

          {/* Preview */}
          <p className="text-white/70 text-sm md:text-base mb-4 line-clamp-2">{article.preview}</p>

          {/* Meta */}
          <div className="flex items-center gap-3 text-sm text-white/60">
            {author?.name && (
              <span className="flex items-center gap-2">
                <AuthorAvatar
                  author={author}
                  sizeClass="w-8 h-8"
                  sizes="32px"
                  ringClass="ring-1 ring-white/25"
                />
                {author.name}
              </span>
            )}
            {author?.name && article.publishedDate && <span>•</span>}
            {article.publishedDate && (
              <span>
                {new Date(article.publishedDate).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </span>
            )}
          </div>
        </div>
      </Link>
    )
  }

  // Large and Standard variants - card style with image on top
  return (
    <Link
      href={`/articles/${article.slug}`}
      className={`group bg-white rounded-2xl overflow-hidden border border-gray-200 hover:shadow-xl transition-all duration-300 flex flex-col ${gridClasses[variant]}`}
    >
      {/* Image - takes proportional space */}
      <div className="relative flex-1 min-h-0 overflow-hidden">
        {imageUrl ? (
          <Image
            src={imageUrl}
            alt={article.title || ''}
            fill
            priority={priority}
            sizes="(max-width: 768px) 100vw, 33vw"
            className="object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 bg-linear-to-br from-gray-100 to-gray-200" />
        )}
      </div>

      {/* Content - fixed height area */}
      <div className="p-4 flex flex-col">
        {/* Tag */}
        {visaTypes && visaTypes.length > 0 && (
          <span
            className={`text-xs font-medium mb-2 ${
              getTagVariant(visaTypes[0].name || '') === 'orange'
                ? 'text-orange-600'
                : getTagVariant(visaTypes[0].name || '') === 'green'
                  ? 'text-green-600'
                  : getTagVariant(visaTypes[0].name || '') === 'blue'
                    ? 'text-blue-600'
                    : 'text-gray-600'
            }`}
          >
            {visaTypes[0].name}
          </span>
        )}

        {/* Title */}
        <h3 className="text-base font-medium text-gray-900 mb-2 group-hover:text-brand-blue transition-colors line-clamp-2">
          {article.title}
        </h3>

        {/* Preview - only show on large cards */}
        {variant === 'large' && (
          <p className="text-gray-600 text-sm mb-2 line-clamp-2">{article.preview}</p>
        )}

        {/* Meta */}
        <div className="flex items-center gap-2 text-xs text-gray-500 mt-auto">
          {author?.name && (
            <span className="flex items-center gap-1.5">
              <AuthorAvatar
                author={author}
                sizeClass="w-6 h-6"
                sizes="24px"
                ringClass="ring-1 ring-gray-200"
              />
              {author.name}
            </span>
          )}
          {author?.name && article.publishedDate && <span>•</span>}
          {article.publishedDate && (
            <span>
              {new Date(article.publishedDate).toLocaleDateString('en-US', {
                month: 'short',
                day: 'numeric',
              })}
            </span>
          )}
        </div>
      </div>
    </Link>
  )
}
